import { ApiError } from '@/lib/api/errors';

/**
 * Qué pasó al escribir un diseño en varios despliegues, y por qué.
 *
 * Puro a propósito: sin React y sin estado. Lo usan el guardado del Design
 * Journey (un evento o todos los de un proyecto) y «Aplicar a…», que tienen el
 * mismo problema con distinto alcance — escribir en N sitios, que alguno diga
 * que no, y contarlo bien.
 *
 * Nace del bucle de 422 de la épica #333 (XCS-15): con un juego y sin Official
 * Rules, cada autoguardado volvía a mandar lo mismo cada 2,5 s y el toast sólo
 * decía «La petición no cumple el contrato», en español para todo el mundo. El
 * porqué de verdad venía en `details[].code` y nadie lo leía.
 *
 * D-6 (misma épica): el AUTOguardado no insiste contra un servidor caído —corta
 * la ronda en el primer fallo pasajero y espera si es un 429— y el texto del
 * servidor sólo se enseña si es una frase para personas (D-4).
 */

/** Cada cuánto reintenta el autoguardado un borrador sucio. */
export const AUTOGUARDADO_MS = 2_500;
/** Espera tras un 429 sin `Retry-After` legible: el bloqueo del limitador (60 s). */
export const ESPERA_429_POR_DEFECTO_MS = 60_000;
/** Tope de la espera pedida por el servidor. */
export const ESPERA_429_MAX_MS = 300_000;

/** El `t()` de `useT`, sin atarse al hook: así esto se prueba sin React. */
export type Traductor = (key: string, vars?: Record<string, string | number>) => string;

export type ResultadoEscritura<T> =
  | { objetivo: T; ok: true }
  // `intentado: false` = la ronda se cortó antes de llegar a este destino.
  | { objetivo: T; ok: false; error: unknown; intentado: boolean };

/** El resultado de un destino que no se guardó. */
export type FalloEscritura<T> = Extract<ResultadoEscritura<T>, { ok: false }>;

/** Para filtrar los fallos sin perder el tipo (`resultados.filter(esFallo)`). */
export function esFallo<T>(r: ResultadoEscritura<T>): r is FalloEscritura<T> {
  return !r.ok;
}

/**
 * Escribe en cada objetivo, EN SERIE y en orden; nunca lanza; un resultado por
 * objetivo.
 *
 * En serie y no con `Promise.allSettled`: es el perfil de carga de siempre —una
 * petición por estación, una detrás de otra— y cambiarlo no hacía falta para
 * arreglar nada. Lo que sí cambia es que un fallo ya no corta la fila: antes el
 * primer error lanzaba y los destinos de detrás ni se intentaban, así que un
 * proyecto quedaba guardado a medias sin que nadie lo dijera.
 */
export async function escribirEnCadaUno<T>(
  objetivos: readonly T[],
  escribir: (o: T) => Promise<unknown>,
  opciones?: { cortarEn?: (error: unknown) => boolean },
): Promise<ResultadoEscritura<T>[]> {
  const resultados: ResultadoEscritura<T>[] = [];
  let corte: { error: unknown } | null = null;
  for (const objetivo of objetivos) {
    if (corte) {
      // Con la ronda cortada los de detrás cuentan como fallos con el mismo error.
      resultados.push({ objetivo, ok: false, error: corte.error, intentado: false });
      continue;
    }
    try {
      await escribir(objetivo);
      resultados.push({ objetivo, ok: true });
    } catch (error) {
      resultados.push({ objetivo, ok: false, error, intentado: true });
      if (opciones?.cortarEn?.(error)) corte = { error };
    }
  }
  return resultados;
}

/**
 * ¿El servidor dijo que NO, o simplemente no contestó bien?
 *
 * Un 4xx (salvo 429) es determinista: volver a mandar lo mismo da lo mismo, y
 * esperar más no lo arregla. Red caída, timeout, 429 y 5xx son pasajeros, y ahí
 * reintentar es justo lo que salva el cambio. Un error que no sea `ApiError` se
 * trata como pasajero: no hay forma de saber que repetirlo no servirá.
 */
export function esRechazo(e: unknown): boolean {
  return e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 429;
}

/** Red caída, timeout, 429, 5xx o un error que no es de la API: reintentar puede servir. */
export function esPasajero(e: unknown): boolean {
  return !esRechazo(e);
}

/** 401 final: el interceptor ya intentó refrescar y ya cerró la sesión. */
export function esSesionCaida(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

/** Política del AUTOguardado y del guardado al salir: corta en pasajero o sesión caída. */
export function cortaAutoguardado(e: unknown): boolean {
  return esPasajero(e) || esSesionCaida(e);
}

/** Política del guardado explícito y de «Aplicar a…»: sólo corta si la sesión cayó. */
export function cortaGuardadoExplicito(e: unknown): boolean {
  return esSesionCaida(e);
}

/** 429 → ms de espera acotados a [AUTOGUARDADO_MS, ESPERA_429_MAX_MS]; otro error → null. */
export function esperaTrasLimite(e: unknown): number | null {
  if (!(e instanceof ApiError) || e.status !== 429) return null;
  const pedida = e.retryAfterMs;
  if (typeof pedida !== 'number' || !Number.isFinite(pedida)) return ESPERA_429_POR_DEFECTO_MS;
  return Math.min(ESPERA_429_MAX_MS, Math.max(AUTOGUARDADO_MS, pedida));
}

/** Los bloqueos legales que el servidor puede devolver al guardar (`severity: 'block'`). */
const MOTIVO_LEGAL: Record<string, string> = {
  XY_LEGAL_OFFICIAL_RULES_MISSING: 'designJourney.saveError.officialRulesMissing',
  XY_LEGAL_AGE_GATE_MISSING: 'designJourney.saveError.ageGateMissing',
  XY_LEGAL_DRAWING_NEEDS_CONTACT: 'designJourney.saveError.drawingNeedsContact',
};

interface DetalleDeError {
  code?: unknown;
  message?: unknown;
}

/**
 * Los `details` del error de dominio de Xurvey, si los trae.
 *
 * El cuerpo es `{ statusCode, error, message, details: [{ field, code, message }] }`
 * (`xurvey-domain.exception.ts`), pero `payload` es `unknown` y puede venir de
 * cualquier otro endpoint con otra forma: se mira con cuidado, sin suponer nada.
 */
function detallesDe(payload: unknown): DetalleDeError[] {
  if (!payload || typeof payload !== 'object') return [];
  const details = (payload as { details?: unknown }).details;
  if (!Array.isArray(details)) return [];
  return details.filter((d): d is DetalleDeError => !!d && typeof d === 'object');
}

/** Mensajes genéricos que los frameworks ponen por defecto: no explican nada. */
const GENERICOS = new Set([
  'la petición no cumple el contrato',
  'bad request',
  'unauthorized',
  'forbidden',
  'forbidden resource',
  'not found',
  'conflict',
  'unprocessable entity',
  'too many requests',
  'internal server error',
  'service unavailable',
  'network error',
]);

/**
 * ¿Es una frase escrita para personas y no una cadena interna del servidor?
 *
 * Una regla de FORMA, determinista y sin idioma: los 4xx sin código conocido
 * son justo los que no están en ninguna lista. Deja fuera lo que parece un
 * identificador (`surveyId=100`, `overrides.design`), un mensaje de
 * class-validator o una traza, y los genéricos del framework. Medido (D-4): un
 * 409 de `surveyLocked` salía como `surveyId=100` y un 400 del ValidationPipe
 * como `overrides.design must be an object`.
 */
export function esFraseParaPersonas(s: unknown): s is string {
  if (typeof s !== 'string') return false;
  const f = s.trim();
  if (f.length < 12 || f.length > 200) return false;
  if (f.split(/\s+/).length < 3) return false;
  const bajo = f.toLowerCase().replace(/\.$/, '');
  if (GENERICOS.has(bajo)) return false;
  if (/^request failed \(\d+\)$/.test(bajo) || /^timeout of \d+ms exceeded$/.test(bajo)) return false;
  if (/[={}[\]<>`\\]/.test(f)) return false;
  if (/\b[A-Za-z_]\w*\.\w/.test(f)) return false;
  if (/\b[a-z]+[A-Z]/.test(f)) return false;
  if (/\b[A-Z][A-Z0-9]*_[A-Z0-9_]+\b/.test(f)) return false;
  if (/[a-z0-9]_[a-z0-9]/i.test(f)) return false;
  if (/^\S+\s+(must|should|is not|are not|has to|needs to)\b/i.test(f) || /^property\s/i.test(f)) {
    return false;
  }
  if (/exception|error:|\bat \S+ \(/i.test(f)) return false;
  return true;
}

/**
 * El porqué, en el idioma de la app.
 *
 * Por orden: un bloqueo legal conocido (el primero que se reconozca gana), el
 * estado HTTP que tiene texto propio y, para otro 4xx, el texto del servidor
 * SÓLO si es una frase para personas (`esFraseParaPersonas`); si no, un
 * genérico propio. Si mañana el servidor añade un código de bloqueo nuevo con
 * una frase decente, sale esa frase sin romper nada.
 */
export function motivoDeError(e: unknown, t: Traductor): string {
  if (!(e instanceof ApiError)) return t('designJourney.saveError.unknown');

  const detalles = detallesDe(e.payload);
  for (const d of detalles) {
    const clave = typeof d.code === 'string' ? MOTIVO_LEGAL[d.code] : undefined;
    if (clave) return t(clave);
  }
  if (e.status === 401) return t('designJourney.saveError.sessionExpired');
  if (e.status === 403) return t('designJourney.saveError.forbidden');
  // 404 también es «de otra empresa»: el módulo nunca confirma que exista.
  if (e.status === 404) return t('designJourney.saveError.notFound');
  if (e.status === 409) return t('designJourney.saveError.conflict');
  if (e.status === 0) return t('designJourney.saveError.network');
  if (e.status === 429) return t('designJourney.saveError.rateLimited');
  if (e.status >= 500) return t('designJourney.saveError.server');

  for (const d of detalles) {
    if (esFraseParaPersonas(d.message)) return d.message.trim();
  }
  if (esFraseParaPersonas(e.message)) return e.message.trim();
  return t('designJourney.saveError.unknown');
}

export interface ResumenEvento {
  eventId: number | null;
  ok: number;
  fallidos: number;
  /** El primer error de ese evento. */
  error?: unknown;
}

/**
 * Agrupa por evento conservando el orden de primera aparición.
 *
 * Desde un proyecto, «se guardó en 3 de 5 enlaces» no dice nada útil: quien
 * diseña piensa en eventos («el de Monterrey no»), no en enlaces de estación.
 */
export function agruparPorEvento<T>(
  resultados: readonly ResultadoEscritura<T>[],
  eventoDe: (o: T) => number | null,
): ResumenEvento[] {
  const grupos = new Map<number | null, ResumenEvento>();
  for (const r of resultados) {
    const eventId = eventoDe(r.objetivo);
    let grupo = grupos.get(eventId);
    if (!grupo) {
      grupo = { eventId, ok: 0, fallidos: 0 };
      grupos.set(eventId, grupo);
    }
    if (r.ok) {
      grupo.ok += 1;
    } else {
      grupo.fallidos += 1;
      if (grupo.fallidos === 1) grupo.error = r.error;
    }
  }
  return [...grupos.values()];
}
